import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';

/**
 * Wiederverwendbare Autocomplete-Komponente für SAP-Stammdaten.
 *
 * Zwei Datenquellen, additiv:
 *   - helpId gesetzt  -> SAP-Wertehilfe /api/value-help/:helpId (live aus SAP, mit
 *                        Schnappschuss-Rueckfall). Kontextabhaengig (z. B. Lagerort je Werk),
 *                        liefert {code,text}. DAS ist der bevorzugte Weg.
 *   - nur type gesetzt -> Alt-Pfad /api/admin/mappings/:type/search (statischer field_mappings-
 *                        Snapshot, {code,name}). Unveraendert, damit bestehende Felder weiterlaufen.
 *
 * Props:
 *   helpId      {string}   Wertehilfe-Id ('unit'|'storage_location'|'gl_account'|...). Vorrang.
 *   context     {object}   Kontext fuer die Wertehilfe (werks/knttp/...). Nur mit helpId.
 *   minChars    {number}   Ab wie vielen Zeichen gesucht wird (0 = auch leer sinnvoll). Default 1.
 *   type        {string}   Alt-Pfad mapping_type. Nur wenn helpId fehlt.
 *   value       {string}   Aktueller Code-Wert (kontrolliertes Input)
 *   onChange    {function} Callback (code: string) — bei freier Eingabe UND Dropdown-Auswahl
 *   onSelect    {function} Optional. Callback (row) NUR bei Dropdown-Auswahl — erhaelt die volle
 *                          Trefferzeile ({code, name, ...weitere SAP-Felder}) fuer abhaengige
 *                          Vorbelegung (z. B. Material -> Kurztext)
 *   placeholder {string}   Placeholder-Text
 *   disabled    {boolean}  Input deaktiviert
 *   isDarkMode  {boolean}  Optional override; sonst aus ThemeContext
 *   inputRef    {React.Ref} Optionale Ref-Weiterleitung an das <input>
 *   className   {string}   Zusaetzliche Container-Klassen
 *   errorBorderClass {string} ring-Klassen bei Validierungsfehler
 *   token       {string}   JWT-Bearer-Token
 */
const MappingAutocomplete = ({
  helpId,
  context,
  minChars,
  type,
  value,
  onChange,
  onSelect,
  placeholder = '',
  disabled = false,
  isDarkMode: isDarkModeProp,
  inputRef: externalInputRef,
  className = '',
  errorBorderClass = '',
  token,
}) => {
  const { isDarkMode: isDarkModeCtx } = useTheme();
  const isDarkMode = isDarkModeProp !== undefined ? isDarkModeProp : isDarkModeCtx;

  const useValueHelp = Boolean(helpId);
  // Wertehilfen mit Pflichtlisten (Einheit, Lagerort) sind auch ohne Eingabe sinnvoll -> 0.
  const effectiveMinChars = typeof minChars === 'number' ? minChars : 1;

  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  // 'snapshot' = SAP war nicht erreichbar, Werte stammen aus dem letzten Abgleich. Wird als
  // dezenter Hinweis angezeigt, statt dem Nutzer eine stille (moeglicherweise veraltete) Liste
  // unterzuschieben.
  const [source, setSource] = useState(null);

  const internalInputRef = useRef(null);
  const inputRef = externalInputRef || internalInputRef;
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // Kontext als stabiler String, damit der useCallback nicht bei jedem Render neu entsteht.
  const contextKey = useValueHelp ? JSON.stringify(context || {}) : '';

  // Hinweiszeile im Dropdown (z. B. "Mindestens 2 Zeichen" beim Material-Browse ohne Eingabe).
  const [hint, setHint] = useState(null);

  const search = useCallback(
    async (query, opts = {}) => {
      const q = query || '';
      // browse = F4-Verhalten (Doppelklick/Lupe): Auswahl OHNE Suchbegriff oeffnen. Der Server
      // begrenzt die Menge (Wertehilfe: erste N aus SAP; Alt-Pfad: /all der gepflegten Werte) —
      // es erscheint also nur, was wirklich gepflegt ist, kein Datensalat.
      const browsing = Boolean(opts.browse);
      if (!useValueHelp && q.length < 1 && !browsing) {
        setResults([]);
        setShowDropdown(false);
        return;
      }
      if (useValueHelp && !browsing && q.length > 0 && q.length < effectiveMinChars) {
        setResults([]);
        setShowDropdown(false);
        return;
      }

      setIsSearching(true);
      try {
        let url;
        if (useValueHelp) {
          const params = new URLSearchParams({ limit: browsing ? '20' : '10' });
          if (q) params.set('q', q);
          const ctx = context || {};
          for (const key of ['werks', 'knttp', 'kokrs', 'bukrs', 'spras']) {
            if (ctx[key]) params.set(key, String(ctx[key]));
          }
          url = `/api/value-help/${encodeURIComponent(helpId)}?${params.toString()}`;
        } else if (browsing && q.length < 1) {
          // Alt-Pfad kennt keine Leersuche -> /all liefert die komplette gepflegte Liste.
          url = `/api/admin/mappings/${encodeURIComponent(type)}/all`;
        } else {
          url = `/api/admin/mappings/${encodeURIComponent(type)}/search?q=${encodeURIComponent(q)}&limit=10`;
        }

        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          // Alt-Pfad liefert ein Array {code,name}; Wertehilfe liefert {rows:[{code,text}], source}.
          // Spread: weitere SAP-Felder der Zeile (z. B. kuenftig meins/matkl beim Material)
          // durchreichen, damit onSelect sie fuer die Vorbelegung nutzen kann.
          const rows = useValueHelp
            ? (data.rows || []).map((r) => ({ ...r, code: r.code, name: r.text }))
            : (Array.isArray(data) ? data : []).slice(0, browsing ? 50 : 10);
          setResults(rows);
          setSource(useValueHelp ? (data.source || null) : null);
          // Browse ohne Treffer nicht stumm lassen: bei Hilfen mit Mindestlaenge (Material,
          // Auftrag, Lieferant) liefert SAP ohne Suchbegriff bewusst nichts — das sagen wir.
          if (browsing && rows.length === 0 && effectiveMinChars > 0) {
            setHint(`Mindestens ${effectiveMinChars} Zeichen eingeben — Liste ist zu groß für eine Gesamtanzeige`);
            setShowDropdown(true);
          } else {
            setHint(null);
            setShowDropdown(rows.length > 0);
          }
        }
      } catch (err) {
        console.error('MappingAutocomplete search error:', err);
      } finally {
        setIsSearching(false);
      }
    },
    // contextKey deckt context ab (stabiler String); helpId/type/token/useValueHelp direkt.
    [useValueHelp, helpId, type, token, effectiveMinChars, contextKey] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // F4-Browse: Doppelklick auf das Feld oder Klick auf die Lupe.
  const openBrowse = () => {
    if (disabled) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    search('', { browse: true });
  };

  // Freie Eingabe: onChange sofort, Suche debounced (250 ms wie bisher).
  const handleInputChange = (e) => {
    const val = e.target.value;
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 250);
  };

  // onFocus: Wertehilfen mit Pflichtliste sofort oeffnen (auch ohne Eingabe); Alt-Pfad nur wenn
  // bereits ein Wert dasteht.
  const handleFocus = () => {
    if (useValueHelp && effectiveMinChars === 0) {
      search(value || '');
    } else if (value) {
      search(value);
    }
  };

  const handleSelect = (result) => {
    onChange(result.code);
    // Volle Trefferzeile nach aussen reichen (code + name + evtl. weitere SAP-Felder wie
    // meins/matkl) — nur bei echter Dropdown-Auswahl, nie bei freier Eingabe. Damit kann der
    // Aufrufer abhaengige Felder vorbelegen (SAP-Standard: MATNR zieht Kurztext nach).
    if (typeof onSelect === 'function') onSelect(result);
    setShowDropdown(false);
    setResults([]);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [inputRef]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <div className={`relative ${className}`}>
      {/* Glas-Hintergrund analog GeneralTab */}
      <div
        className={`absolute inset-0 ${
          isDarkMode
            ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]'
            : 'bg-white border border-slate-200'
        } rounded-2xl ${errorBorderClass}`}
      />

      {/* Input + Spinner/Lupe */}
      <div className="relative flex items-center">
        <input
          ref={inputRef}
          type="text"
          value={value || ''}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onDoubleClick={openBrowse}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className={`relative w-full px-3 py-2 pr-8 bg-transparent border-0 rounded-2xl
            focus:ring-2 focus:ring-brand/40 focus:outline-none transition-all duration-300
            ${isDarkMode ? 'text-white placeholder-white/60' : 'text-slate-800 placeholder-slate-400'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        />
        {isSearching ? (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div
              className={`w-4 h-4 border-2 ${
                isDarkMode
                  ? 'border-white/30 border-t-white'
                  : 'border-slate-300 border-t-slate-600'
              } rounded-full animate-spin`}
            />
          </div>
        ) : (
          <button
            type="button"
            tabIndex={-1}
            onClick={openBrowse}
            disabled={disabled}
            title="Auswahl öffnen (F4)"
            className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer"
          >
            <Search
              className={`w-4 h-4 ${
                isDarkMode ? 'text-white/40 hover:text-white/80' : 'text-slate-400 hover:text-slate-600'
              }`}
            />
          </button>
        )}
      </div>

      {/* Dropdown — auch bei 0 Treffern sichtbar, wenn es einen Hinweis gibt (F4-Browse auf
          Hilfen mit Mindestlaenge). max-h + Scroll, weil der Browse bis zu 50 Zeilen liefert. */}
      {showDropdown && (results.length > 0 || hint) && (
        <div
          ref={dropdownRef}
          className={`absolute z-50 w-full mt-1 rounded-xl overflow-hidden shadow-lg max-h-72 overflow-y-auto ${
            isDarkMode
              ? 'bg-gray-800/95 border border-white/20 backdrop-blur-xl'
              : 'bg-white border border-slate-200'
          }`}
        >
          {hint && (
            <div
              className={`px-4 py-2 text-xs ${
                isDarkMode ? 'text-white/60' : 'text-slate-500'
              }`}
            >
              {hint}
            </div>
          )}
          {/* Herkunftshinweis: nur wenn die Werte aus dem Ausfall-Schnappschuss stammen. */}
          {source === 'snapshot' && (
            <div
              className={`px-4 py-1.5 text-xs ${
                isDarkMode
                  ? 'bg-amber-900/40 text-amber-300 border-b border-white/10'
                  : 'bg-amber-50 text-amber-700 border-b border-amber-100'
              }`}
            >
              SAP nicht erreichbar — Werte aus dem letzten Abgleich
            </div>
          )}
          {results.map((result, index) => (
            <button
              key={`${result.code}-${index}`}
              type="button"
              onClick={() => handleSelect(result)}
              className={`w-full px-4 py-3 text-left transition-all duration-150 flex items-center justify-between ${
                isDarkMode
                  ? 'hover:bg-brand/20 text-white border-b border-white/10 last:border-b-0'
                  : 'hover:bg-slate-50 text-slate-800 border-b border-slate-100 last:border-b-0'
              }`}
            >
              <div className="flex items-center space-x-3">
                <span
                  className={`font-mono font-semibold text-sm ${
                    isDarkMode ? 'text-brand' : 'text-brand'
                  }`}
                >
                  {result.code}
                </span>
                <span
                  className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}
                >
                  {result.name}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MappingAutocomplete;
